import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Modal from "../Common/Modal";
import { ACTION_DISPLAY_TYPES, ACTION_TYPES } from "../../constants/enums";
import { getNumericOverride } from "../../services/utilityFunctions";

interface SessionEndModalProps {
  show?: boolean;
  secondsRemaining?: number;
  showWithSecondsRemaining?: number;
}

const SessionEndModal = ({
  show = false,
  secondsRemaining: initialSeconds = 1200,
  showWithSecondsRemaining = 60,
}: SessionEndModalProps) => {
  const history = useHistory();
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  // allow overriding session length and warning threshold via URL parameters for debugging purposes
  const overrideLength = getNumericOverride("sessionTimeout");
  const overrideWarning = getNumericOverride("sessionWarning");

  const sessionLength = overrideLength ?? initialSeconds;
  const threshold = overrideLength
    ? Math.min(overrideWarning ?? 10, Math.floor(sessionLength / 2))
    : showWithSecondsRemaining;

  const [secondsRemaining, setSecondsRemaining] = useState(sessionLength);
  const [showModal, setShowModal] = useState(show);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSecondsRemaining((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, []);

  useEffect(() => {
    if (secondsRemaining <= 0) {
      clearInterval(intervalRef.current);
      history.push("/");
      return;
    }

    setShowModal(secondsRemaining <= threshold);
  }, [secondsRemaining, threshold, history]);

  const stayConnected = () => {
    setSecondsRemaining(sessionLength);
    setShowModal(false);
  };

  const logOutNow = () => {
    clearInterval(intervalRef.current);
    setShowModal(false);
    history.push("/");
  };

  return (
    <Modal
      open={showModal}
      onClose={stayConnected}
      title="Session Ending"
      closeOnOverlayClick={false}
      className="v2 v3"
      content={
        <>
          You will be automatically logged out in {secondsRemaining}.
          <br />
          <br />
          Your session is about to expire.
        </>
      }
      actions={[
        {
          title: "Stay Connected",
          displayType: ACTION_DISPLAY_TYPES.SECONDARY,
          actionType: ACTION_TYPES.DISMISS,
          onClick: stayConnected,
          focused: true,
        },
        {
          title: "Log Out Now",
          displayType: ACTION_DISPLAY_TYPES.PRIMARY,
          actionType: ACTION_TYPES.DISMISS,
          onClick: logOutNow,
        },
      ]}
    />
  );
};

export default SessionEndModal;